<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { LoaderCircle, Send, Sparkles, X } from '@lucide/vue'
import { summarizeDecorationAiOperation, type DecorationAiOperation } from './decoration-ai-summary'

interface DecorationAiMessage {
  id: string
  role: 'user' | 'ai'
  text: string
  operations?: DecorationAiOperation[]
  resultSchemaJson?: string
  /** 生成该结果时基于的画布 schema，用于应用前的同页漂移检测 */
  baseSchemaJson?: string
}

const props = defineProps<{
  open: boolean
  pageCode: string
  moduleCode: string
  /** 返回当前画布草稿的 schema JSON 文本，发送时作为上下文传给后端 */
  getSchemaJson: () => string
  /** 由父组件注入的发送实现（Manager 侧封装 chatSaasAgent），面板不直接持有 apiClient */
  send: (message: string, context: Record<string, unknown>) => Promise<unknown>
}>()

const emit = defineEmits<{
  close: []
  apply: [resultSchemaJson: string]
}>()

const prompt = ref('')
const sending = ref(false)
const error = ref('')
const messages = ref<DecorationAiMessage[]>([])
const messageListRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
let messageSequence = 0

const canSend = computed(() => prompt.value.trim().length > 0 && !sending.value)

watch(() => props.open, (open) => {
  if (!open) return
  error.value = ''
  void focusAfterRender()
})

watch(() => [props.pageCode, props.moduleCode], () => {
  // 切换页面/模块后旧对话的 resultSchemaJson 不再适用，避免误应用到新页面
  messages.value = []
  error.value = ''
  prompt.value = ''
})

function nextMessageId(prefix: string) {
  messageSequence += 1
  return `${prefix}-${Date.now()}-${messageSequence}`
}

async function sendMessage() {
  const text = prompt.value.trim()
  if (!text || sending.value) return
  prompt.value = ''
  error.value = ''
  sending.value = true
  const baseSchemaJson = props.getSchemaJson()
  messages.value = [...messages.value, { id: nextMessageId('user'), role: 'user', text }]
  await scrollToBottom()
  try {
    const response = await props.send(text, {
      surface: 'decoration',
      moduleCode: props.moduleCode,
      pageCode: props.pageCode,
      schemaJson: baseSchemaJson
    })
    messages.value = [...messages.value, buildAiMessage(response, baseSchemaJson)]
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'AI 装修请求失败，请稍后重试。'
    prompt.value = text
  } finally {
    sending.value = false
    await scrollToBottom()
  }
}

function buildAiMessage(response: unknown, baseSchemaJson: string): DecorationAiMessage {
  const data = isRecord(response) && isRecord(response.data) ? response.data : {}
  const operations = extractOperations(data.operations)
  const resultSchemaJson = typeof data.resultSchemaJson === 'string' && data.resultSchemaJson.trim()
    ? data.resultSchemaJson
    : undefined
  const answer = isRecord(response) && typeof response.answer === 'string' ? response.answer.trim() : ''
  const appliedSummary = Array.isArray(data.appliedSummary)
    ? data.appliedSummary.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
    : []
  const text = answer || (appliedSummary.length ? appliedSummary.join('；') : '已处理。')
  return {
    id: nextMessageId('ai'),
    role: 'ai',
    text,
    operations: operations.length ? operations : undefined,
    resultSchemaJson,
    baseSchemaJson
  }
}

function extractOperations(value: unknown): DecorationAiOperation[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is DecorationAiOperation =>
    isRecord(item) && typeof item.op === 'string' && Boolean(item.op))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function applyResult(message: DecorationAiMessage) {
  if (!message.resultSchemaJson || sending.value) return
  // 同页防呆：对话后画布被手动改过就不允许覆盖，提示重新生成
  if (message.baseSchemaJson !== undefined && message.baseSchemaJson !== props.getSchemaJson()) {
    error.value = '画布内容在对话后已被修改，请基于当前页面重新生成后再应用。'
    return
  }
  emit('apply', message.resultSchemaJson)
}

function closePanel() {
  emit('close')
}

function handleEnterKey(event: KeyboardEvent) {
  // Safari 下中文 IME 组合中按回车选词会触发 keydown.enter，组合未结束时不应发送
  if (event.isComposing) return
  void sendMessage()
}

function handlePanelKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && !event.defaultPrevented && !sending.value) {
    closePanel()
    return
  }
  if (event.key !== 'Tab') return
  // 简易焦点圈定：Tab 在面板可聚焦元素内循环
  const root = panelRef.value
  if (!root) return
  const focusables = Array.from(
    root.querySelectorAll<HTMLElement>('button, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((el) => !el.hasAttribute('disabled'))
  if (!focusables.length) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement as HTMLElement | null
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

async function focusAfterRender() {
  await nextTick()
  closeButtonRef.value?.focus()
  await scrollToBottom()
}

async function scrollToBottom() {
  await nextTick()
  messageListRef.value?.scrollTo({
    top: messageListRef.value.scrollHeight,
    behavior: 'smooth'
  })
}
</script>

<template>
  <Teleport to="body">
    <div v-if="props.open" class="decoration-ai-panel__mask">
      <button type="button" class="decoration-ai-panel__scrim" aria-label="关闭 AI 装修对话" @click="closePanel"></button>
      <section
        ref="panelRef"
        class="decoration-ai-panel"
        role="dialog"
        aria-modal="true"
        aria-label="AI 装修对话"
        tabindex="-1"
        @keydown="handlePanelKeydown"
      >
        <header class="decoration-ai-panel__header">
          <div>
            <p><Sparkles :size="13" aria-hidden="true" /> AI 装修</p>
            <h2>描述想要的调整，AI 给出变更清单</h2>
            <span>当前页面 {{ props.pageCode }} · 应用到画布后需手动保存才生效</span>
          </div>
          <button
            ref="closeButtonRef"
            type="button"
            class="decoration-ai-panel__close"
            title="关闭"
            aria-label="关闭"
            @click="closePanel"
          >
            <X :size="18" aria-hidden="true" />
          </button>
        </header>

        <div ref="messageListRef" class="decoration-ai-panel__messages">
          <p v-if="!messages.length" class="decoration-ai-panel__empty">
            例如：「把主题色改成青玉色 #0F766E」「新增一个公告栏组件」。AI 会先列出变更清单，确认后再应用到画布。
          </p>
          <article
            v-for="message in messages"
            :key="message.id"
            class="decoration-ai-panel__message"
            :class="message.role === 'user' ? 'is-user' : 'is-ai'"
          >
            <p>{{ message.text }}</p>
            <ul v-if="message.operations?.length" class="decoration-ai-panel__operations">
              <li v-for="(operation, index) in message.operations" :key="`${message.id}-op-${index}`">
                {{ summarizeDecorationAiOperation(operation) }}
              </li>
            </ul>
            <footer v-if="message.resultSchemaJson" class="decoration-ai-panel__apply">
              <button type="button" class="is-primary" @click="applyResult(message)">
                应用到画布
              </button>
            </footer>
          </article>
          <p v-if="sending" class="decoration-ai-panel__thinking">
            <LoaderCircle :size="14" class="is-spinning" aria-hidden="true" /> AI 正在生成变更清单...
          </p>
        </div>

        <p v-if="error" class="decoration-ai-panel__error" role="alert">{{ error }}</p>

        <form class="decoration-ai-panel__composer" @submit.prevent="sendMessage">
          <textarea
            v-model="prompt"
            rows="3"
            placeholder="描述想要的装修调整，回车发送"
            aria-label="装修调整描述"
            :disabled="sending"
            @keydown.enter.exact.prevent="handleEnterKey"
          ></textarea>
          <button type="submit" :disabled="!canSend">
            <Send :size="14" aria-hidden="true" />
            {{ sending ? '生成中' : '发送' }}
          </button>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.decoration-ai-panel__mask {
  position: fixed;
  inset: 0;
  z-index: 90;
}

.decoration-ai-panel__scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(15, 23, 42, 0.24);
  cursor: pointer;
}

.decoration-ai-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  width: min(460px, 100vw);
  height: 100vh;
  box-sizing: border-box;
  border-left: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  box-shadow: -18px 0 44px rgba(15, 23, 42, 0.14);
}

.decoration-ai-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid var(--rr-color-border);
  background: #ffffff;
  padding: 16px;
}

.decoration-ai-panel__header p {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 4px;
  color: var(--rr-color-primary-hover);
  font-size: 12px;
  font-weight: 800;
}

.decoration-ai-panel__header h2 {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 16px;
  line-height: 1.3;
}

.decoration-ai-panel__header span {
  display: block;
  margin-top: 4px;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.decoration-ai-panel__close {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--rr-color-text);
  cursor: pointer;
}

.decoration-ai-panel__close:hover {
  background: var(--rr-color-surface-subtle);
}

.decoration-ai-panel__messages {
  display: grid;
  align-content: start;
  gap: 10px;
  overflow: auto;
  padding: 14px;
}

.decoration-ai-panel__empty {
  margin: 0;
  border: 1px dashed var(--rr-color-border-strong);
  border-radius: 8px;
  background: #ffffff;
  color: var(--rr-color-text-muted);
  font-size: 13px;
  line-height: 1.6;
  padding: 12px;
}

.decoration-ai-panel__message {
  display: grid;
  gap: 8px;
  max-width: 94%;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: #ffffff;
  color: var(--rr-color-text);
  font-size: 14px;
  line-height: 1.55;
  padding: 10px 12px;
}

.decoration-ai-panel__message p {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.decoration-ai-panel__message.is-user {
  justify-self: end;
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
}

.decoration-ai-panel__message.is-ai {
  justify-self: start;
}

.decoration-ai-panel__operations {
  display: grid;
  gap: 4px;
  margin: 0;
  border-left: 3px solid var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  padding: 8px 10px;
  list-style: none;
}

.decoration-ai-panel__apply {
  display: flex;
  justify-content: flex-end;
}

.decoration-ai-panel__apply button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--rr-color-primary);
  border-radius: 7px;
  background: var(--rr-color-primary);
  color: #ffffff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  padding: 6px 12px;
}

.decoration-ai-panel__apply button:hover {
  background: var(--rr-color-primary-hover);
}

.decoration-ai-panel__thinking {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.decoration-ai-panel__thinking .is-spinning {
  animation: decoration-ai-panel-spin 1s linear infinite;
}

@keyframes decoration-ai-panel-spin {
  to {
    transform: rotate(360deg);
  }
}

.decoration-ai-panel__error {
  margin: 0;
  border-top: 1px solid var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}

.decoration-ai-panel__composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px;
  gap: 10px;
  border-top: 1px solid var(--rr-color-border);
  background: #ffffff;
  padding: 12px;
}

.decoration-ai-panel__composer textarea {
  width: 100%;
  min-height: 72px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  color: var(--rr-color-text);
  resize: none;
  padding: 10px;
}

.decoration-ai-panel__composer button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  border-radius: 6px;
  background: var(--rr-color-primary);
  color: #ffffff;
  cursor: pointer;
  font-weight: 900;
}

.decoration-ai-panel__composer button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

@media (max-width: 640px) {
  .decoration-ai-panel {
    width: 100vw;
  }
}
</style>
